import {Box, BoxProps, Button, Typography,LinearProgress} from "@mui/material"
import {FC} from "react"
import {API} from "@buildwitt/shapeshift-identity";
import {makeStyles} from "tss-react/mui";
import type {ILesson, IModuleScheduled} from "@buildwitt/shapeshift-types";
import type {ICourseModule} from "@buildwitt/shapeshift-types/ICourse";
import {Check, CheckBox, Quiz} from "@mui/icons-material";
import {WYSIWYG} from "@buildwitt/shapeshift-common/v2/wysiwyg";
import {Link} from "react-router-dom";
import {palette} from "../../../constants";
import {getFontStyles} from "../../../templates/defaultTheme";
import VideoPreview from "../VideoPreview";
import {useSSTranslation} from "../../../i18n/useSSTranslation";

interface IProps extends BoxProps {
	module: IModuleScheduled
}

const useStyles = makeStyles<{complete: boolean}>()((theme, {complete}) => ({
	itemContent: {
		display: "flex",
		flexDirection: "column",
		borderRadius: "16px",
		textAlign: "left",
		overflow: "hidden",
		boxSizing: "border-box",
		width: "calc(25% - 15px)",
		maxWidth: "calc(25% - 15px)",
		background: theme.palette.background.paper,
		padding: 0,
		flexGrow: 2,
		position: "relative",
		transition: "transform 0.3s ease-out",
		[theme.breakpoints.down("xl")]: {
			width: "calc(33.3% - 15px)",
			maxWidth: "340px"
		},
		[theme.breakpoints.down("md")]: {
			width: "calc(50% - 15px)"
		},
		[theme.breakpoints.down("sm")]: {
			width: "calc(100% - 15px)"
		},
		"[class*='previewImageSvg']": {
			width: "32px!important",
			height: "32px",
			svg: {
				width: "44px",
				height: "44px",
				transform: "translate(-6px, -6px)",
				marginRight: "auto",
				marginLeft: "auto"
			}
		},
		"& > *": {
			opacity: complete ? 0.25 : 1
		},
		"&:hover": {
			"[class*='btn']": {
				background: palette.orange,
				transition: "0.3s"
			}
		}
	},
	completedCheck: {
		opacity: 1,
		position: "absolute",
		top: 10,
		right: 10,
		zIndex: 10,
		"& svg": {
			width: "35px",
			height: "35px",
			border: "2px solid " + theme.palette.primary.main,
			borderRadius: "100%",
			padding: "10px",
			"& path": {
				fill: theme.palette.primary.main
			}
		}
	},
	title: {
		marginBottom: "0",
		overflow: "hidden",
		textOverflow: "ellipsis",
		display: "-webkit-box",
		WebkitLineClamp: "3",
		WebkitBoxOrient: "vertical"
	},
	itemSubTitle: {
		fontSize: "17px",
		overflow: "hidden",
		textOverflow: "ellipsis",
		display: "-webkit-box",
		WebkitLineClamp: "2",
		WebkitBoxOrient: "vertical"
	},
	description: {
		height: "67px",
		margin: "7px 0",
		color: theme.palette.text.primary,
		display: "-webkit-box",
		webkitLineClamp: "3",
		lineClamp: "3",
		webkitBoxOrient: "vertical",
		overflow: "hidden",
		textOverflow: "ellipsis",
		"*": {
			...getFontStyles(theme.typography.body2),
			margin: 0,
			padding: 0
		},
		"h1,h2,h3,h4,h5,iframe, ul, img": {
			display: "none"
		}
	},
	chip: {
		fontWeight: 600
	},
	blankPreview: {
		width: "calc(100% + 60px)",
		height: "220px",
		top: "0",
		position: "relative",
		left: "-30px",
		overflow: "hidden",
		backgroundColor: theme.palette.primary.main,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		svg: {
			fill: theme.palette.common.white,
			width: "38px",
			height: "38px"
		}
	},
	blankButton: {
		backgroundColor: "transparent",
		padding: "0px",
		border: "none",
		width: "100%",
		lineHeight: "1em",
		height: "220px",
		":hover": {
			backgroundColor: "transparent",
			border: "none"
		}
	},
	cardContents: {
		display: "flex",
		flexFlow: "wrap",
		height: "calc(100% - 220px)",
		padding: "25px 30px",
		boxSizing: "border-box"
	},
	itemDetailsContainer: {
		margin: "5px 0 15px",
		border: "none",
		flexGrow: 3,
		display: "flex",
		gap: "10px",
		flexDirection: "column",
		"& h3": {
			[theme.breakpoints.down("sm")]: {
				fontSize: "19px"
			}
		}

	},
	cardBottom: {
		marginTop: "auto",
		width: "100%",
		paddingTop: "8px",
		marginBottom: "8px"
	},
	itemLink: {
		display: "flex",
		flexFlow: "wrap",
		height: "100%"
	},
	btn: {
		marginTop: "20px",
		width: "100%",
		"&:hover": {
			opacity: 1
		}
	}
}));


export const TrainingFeedAnnouncement:FC<IProps> = ({className, module}) => {
	const {classes, cx} = useStyles({complete: module.complete});
	const {t} = useSSTranslation();

	const getModuleDescription = (module: ICourseModule | null): string => {
		return module?.subtitle ?? "<p></p>";
	}

	const getVideoUrl = (module: ICourseModule | null): string | undefined => {
		if (module?.type === "lesson") {
			const videoUrl = (module as ILesson).videoUrl
			return videoUrl
		}
		return undefined;
	}

	const getVideoTime = (module: ICourseModule | null): number | undefined => {
		if (module?.type === "lesson") {
			return (module as ILesson).videoTime
		}
		return undefined;
	}

	const getVideoLength = (module: ICourseModule | null): number | undefined => {
		if (module?.type === "lesson") {
			return (module as ILesson).videoLength
		}
		return undefined;
	}

	const getProgress = (module: ICourseModule | null): number => {

		if (module?.complete) {
			return 100;
		}

		if (module?.type === "lesson") {
			const videoTime = getVideoTime(module);
			const videoLength = getVideoLength(module);
			if (!videoTime || !videoLength) {
				return 0;
			}
			return Math.min(Math.round((videoTime / videoLength) * 100), 100);
		}

		return 0;
	}

	const getVideoThumbnail = (module: ICourseModule | null): string | undefined => {
		if (module?.type === "lesson") {
			const lesson = module as ILesson;

			if (lesson.videoThumbnailUrl) {
				return lesson.videoThumbnailUrl;
			}

			return "/holding_image.jpg"
		}

		return undefined;
	};

	const progress = getProgress(module);
	const videoUrl = getVideoUrl(module);
	const videoThumbnail = getVideoThumbnail(module);

	return (
		<Box
			className={cx(classes.itemContent, className)}
			data-test-id="training-feed-announcement-card"
			role="article"
			aria-label={`Announcement: ${module.title}`}
		>
			<Link
				className={classes.itemLink}
				to={"/feed/announcement/" + module.slug}
				data-test-id="training-feed-announcement-link"
				aria-label={`Open announcement ${module.title}`}
			>

				{module.complete && <Box
					className={classes.completedCheck}
					data-test-id="training-feed-announcement-completed-check"
					aria-label="Announcement completed"
					role="img"
				>
					<Check/>
				</Box>}
				<Box
					className={classes.blankButton}
					data-test-id="training-feed-announcement-preview"
					aria-label="Announcement preview"
				>
					{(videoUrl && videoThumbnail)
						? <VideoPreview videoUrl={videoThumbnail} />
						: <Box className={classes.blankPreview} aria-label="Announcement icon">
							{module.type === "survey" ? <CheckBox/> : <Quiz />}
						</Box>
					}
				</Box>

				<Box className={classes.cardContents}>
					<Box className={classes.itemDetailsContainer}>
						<Typography
							className={classes.title}
							variant="h4"
							data-test-id="training-feed-announcement-title"
							aria-level={4}
						>
							{module.title}
						</Typography>
						{module?.course?.title &&
							<Typography
								className={classes.itemSubTitle}
								variant="h5"
								data-test-id="training-feed-announcement-course-title"
								aria-label={`Course: ${module.course.title}`}
							>
								In {module.course.title}
							</Typography>
						}
						<WYSIWYG
							className={classes.description}
							data-test-id="training-feed-announcement-description"
							aria-label="Announcement description"
						>
							{getModuleDescription(module)}
						</WYSIWYG>
					</Box>

					<Box className={classes.cardBottom}>
						<Button
							className={classes.btn}
							data-test-id="training-feed-announcement-action-button"
							aria-label={module.type === "lesson" ? `Watch lesson: ${module.title}` : `Learn more about ${module.title}`}
						>
							{module.type === "lesson" ? t("training_feed_watch_lesson", "Watch lesson") : "LEARN MORE"}
						</Button>
					</Box>
				</Box>
			</Link>
		</Box>
	)
}